import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  Search, 
  Filter,
  Trash2, 
  Edit2, 
  Loader2,
  X,
  Calendar,
  FileText,
  ArrowRight,
  MoreVertical,
  AlertCircle,
  CheckCircle2,
  History,
  Download
} from 'lucide-react';
import { collection, 
  addDoc, 
  onSnapshot, 
  query, 
  orderBy, 
  where,
  serverTimestamp,
  updateDoc,
  doc,
  deleteDoc,
  getDocs } from '../firebase';
import { db, auth, handleFirestoreError, OperationType } from '../firebase';
import HistoryNotesView from './HistoryNotesView';
import { notify } from '../services/notify';
import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import ConfirmModal from './ConfirmModal';
import { ManualJournal, JournalLine } from '../types';
import { formatCurrency, getDefaultCurrency } from '../lib/currency';

function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

const getLocalAuthToken = () =>
  typeof window !== 'undefined' ? localStorage.getItem('app_auth_token') : null;

const exportToCSV = (data: any[], filename: string) => {
  if (data.length === 0) return;
  const headers = Object.keys(data[0]);
  const csvRows = [
    headers.join(','),
    ...data.map(row => headers.map(header => {
      const val = row[header];
      return typeof val === 'string' ? `"${val.replace(/"/g, '""')}"` : val;
    }).join(','))
  ];
  const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  if (link.download !== undefined) {
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
};

import { v4 as uuidv4 } from 'uuid';

export default function ManualJournalsView({ orgId, orgSettings, language = 'en', initialAccountId }: { language?: string, orgId: string, orgSettings?: any, initialAccountId?: string | null }) {
  const currency = getDefaultCurrency(orgSettings);
  const [journals, setJournals] = useState<ManualJournal[]>([]);
  const [accounts, setAccounts] = useState<any[]>([]);
  const [contacts, setContacts] = useState<any[]>([]);
  const [journalBooks, setJournalBooks] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedJournal, setSelectedJournal] = useState<ManualJournal | null>(null);
  const [searchTerm, setSearchTerm] = useState(initialAccountId || '');
  const [statusFilter, setStatusFilter] = useState<string>('All');
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [minAmount, setMinAmount] = useState('');
  const [maxAmount, setMaxAmount] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [repeatEnabled, setRepeatEnabled] = useState(false);
  const [repeatFrequency, setRepeatFrequency] = useState(1);
  const [repeatUnit, setRepeatUnit] = useState<'Week' | 'Month'>('Month');
  const [repeatEndDate, setRepeatEndDate] = useState('');
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [isQuickAccountModalOpen, setIsQuickAccountModalOpen] = useState(false);
  const [quickAccountData, setQuickAccountData] = useState({ name: '', code: '', type: 'Expense', description: '' });
  const [targetLineIndex, setTargetLineIndex] = useState(-1);

  const [newJournal, setNewJournal] = useState<Omit<ManualJournal, 'id' | 'createdAt' | 'createdBy'>>({
    journalNumber: '',
    journalBookId: '',
    journalBookName: '',
    date: new Date().toISOString().split('T')[0],
    narration: '',
    status: 'Draft',
    lines: [
      { id: uuidv4(), accountId: '', accountName: '', description: '', debit: 0, credit: 0 },
      { id: uuidv4(), accountId: '', accountName: '', description: '', debit: 0, credit: 0 }
    ],
    totalDebit: 0,
    totalCredit: 0
  });

  const [taxRates, setTaxRates] = useState<any[]>([]);

  useEffect(() => {
    const fetchTaxRates = async () => {
      if (!orgId) return;
      const q = query(collection(db, 'tax_rates'), where('orgId', '==', orgId));
      onSnapshot(q, (snapshot) => {
        setTaxRates(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      });
    };
    fetchTaxRates();
  }, [orgId]);

  useEffect(() => {
    const handleQuickAction = (e: any) => {
      if (e.detail?.action === 'new-journal') {
        setIsEditing(false);
        setNewJournal({
          journalNumber: '',
          journalBookId: '',
          journalBookName: '',
          date: new Date().toISOString().split('T')[0],
          narration: '',
          status: 'Draft',
          lines: [
            { id: uuidv4(), accountId: '', accountName: '', description: '', debit: 0, credit: 0, taxRate: '0%' },
            { id: uuidv4(), accountId: '', accountName: '', description: '', debit: 0, credit: 0, taxRate: '0%' }
          ],
          totalDebit: 0,
          totalCredit: 0
        });
        setIsModalOpen(true);
      }
    };
    window.addEventListener('QUICK_ACTION', handleQuickAction as EventListener);
    return () => window.removeEventListener('QUICK_ACTION', handleQuickAction as EventListener);
  }, []);

  useEffect(() => {
    if (!orgId) return;

    const localAuthToken = getLocalAuthToken();
    if (localAuthToken) {
      let cancelled = false;

      const fetchLocalJournals = async () => {
        try {
          const response = await fetch(`/api/organizations/${orgId}/manual-journals`, {
            headers: { Authorization: `Bearer ${localAuthToken}` }
          });
          if (!response.ok) {
            throw new Error((await response.text()) || `Request failed (${response.status})`);
          }

          const payload = await response.json();
          const localJournals = (Array.isArray(payload) ? payload : payload?.journals || payload?.data || []).map((journal: any) => {
            const lines = (journal.lines || []).map((line: any) => ({
              id: line.id,
              accountId: line.accountId || line.account_id || '',
              accountName: line.accountName || line.account_name || '',
              description: line.description || '',
              debit: Number(line.debit || 0),
              credit: Number(line.credit || 0)
            }));
            const { totalDebit, totalCredit } = calculateTotals(lines);

            return {
              id: journal.id,
              journalNumber: journal.journalNumber || journal.journal_number || '',
              journalBookId: journal.journalBookId || journal.journal_book_id || '',
              journalBookName: journal.journalBookName || journal.journal_book_name || '',
              date: typeof journal.date === 'string' ? journal.date.slice(0, 10) : journal.date,
              narration: journal.narration || journal.description || '',
              status: journal.status ? `${journal.status.charAt(0).toUpperCase()}${journal.status.slice(1)}` : 'Draft',
              lines,
              totalDebit,
              totalCredit,
              createdAt: journal.createdAt || journal.created_at,
              createdBy: journal.createdBy || journal.created_by
            } as ManualJournal;
          });

          if (!cancelled) setJournals(localJournals);
        } catch (error) {
          console.error("Error fetching local journals:", error);
        } finally {
          if (!cancelled) setIsLoading(false);
        }
      };

      fetchLocalJournals();
      return () => { cancelled = true; };
    }

    const q = query(
      collection(db, 'manual_journals'),
      where('orgId', '==', orgId),
      orderBy('date', 'desc')
    );
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const data = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as ManualJournal[];
      setJournals(data);
      setIsLoading(false);
    }, (error) => {
      console.error("Error fetching journals:", error);
      setIsLoading(false);
    });

    return () => unsubscribe();
  }, [orgId]);

  useEffect(() => {
    if (!orgId) return;
    const fetchAccounts = async () => {
      const localAuthToken = getLocalAuthToken();
      if (localAuthToken) {
        const response = await fetch(`/api/organizations/${orgId}/chart-of-accounts`, {
          headers: { Authorization: `Bearer ${localAuthToken}` }
        });
        if (!response.ok) {
          throw new Error((await response.text()) || `Request failed (${response.status})`);
        }
        const payload = await response.json();
        const localAccounts = Array.isArray(payload) ? payload : payload?.accounts || payload?.chartOfAccounts || payload?.data || [];
        setAccounts([...localAccounts].sort((a: any, b: any) => (a.code || '').localeCompare(b.code || '')));
        return;
      }

      const q = query(
        collection(db, 'chart_of_accounts'), 
        where('orgId', '==', orgId),
        orderBy('code', 'asc')
      );
      const snapshot = await getDocs(q);
      setAccounts(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    };
    const fetchContacts = async () => {
      const q = query(collection(db, 'contacts'), where('orgId', '==', orgId));
      const snapshot = await getDocs(q);
      setContacts(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    };
    const fetchJournalBooks = async () => {
      const q = query(collection(db, 'accounting_journals'), where('orgId', '==', orgId));
      const snapshot = await getDocs(q);
      setJournalBooks(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    };
    fetchAccounts();
    fetchContacts();
    fetchJournalBooks();
  }, [orgId]);

  const handleQuickAccountCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const docRef = await addDoc(collection(db, 'chart_of_accounts'), {
        ...quickAccountData,
        orgId,
        status: 'Active',
        createdAt: serverTimestamp(),
        createdBy: auth.currentUser?.uid
      });
      setIsQuickAccountModalOpen(false);
      setQuickAccountData({ name: '', code: '', type: 'Expense', description: '' });
      notify.confirm("Account created");
      
      // Update line with new account immediately
      if (targetLineIndex >= 0) {
        updateLine(targetLineIndex, 'accountId', docRef.id);
        const newLines = [...newJournal.lines];
        newLines[targetLineIndex].accountName = `${quickAccountData.code} - ${quickAccountData.name}`;
        setNewJournal({ ...newJournal, lines: newLines });
      }
    } catch (error) {
      handleFirestoreError(error, OperationType.CREATE, 'chart_of_accounts');
    }
  };

  const calculateTotals = (lines: JournalLine[]) => {
    const totalDebit = lines.reduce((sum, line) => sum + (line.debit || 0), 0);
    const totalCredit = lines.reduce((sum, line) => sum + (line.credit || 0), 0);
    return { totalDebit, totalCredit };
  };

  const addLine = () => {
    let defaultDebit = 0;
    let defaultCredit = 0;
    const diff = newJournal.totalDebit - newJournal.totalCredit;
    if (diff > 0) defaultCredit = diff;
    else if (diff < 0) defaultDebit = Math.abs(diff);

    setNewJournal({
      ...newJournal,
      lines: [...newJournal.lines, { id: uuidv4(), accountId: '', accountName: '', description: '', debit: defaultDebit, credit: defaultCredit, taxRate: '0%' }]
    });
  };

  const removeLine = (index: number) => {
    const newLines = newJournal.lines.filter((_, i) => i !== index);
    const { totalDebit, totalCredit } = calculateTotals(newLines);
    setNewJournal({ ...newJournal, lines: newLines, totalDebit, totalCredit });
  };

  const updateLine = (index: number, field: keyof JournalLine, value: any) => {
    let newLines = [...newJournal.lines];
    if (field === 'accountId') {
      const account = accounts.find(a => a.id === value);
      newLines[index] = { ...newLines[index], accountId: value, accountName: account?.name || '' };
    } else if (field === 'contactId') {
      const contact = contacts.find(c => c.id === value);
      newLines[index] = { ...newLines[index], contactId: value, contactName: contact?.name || '' };
    } else {
      newLines[index] = { ...newLines[index], [field]: value };
    }

    // If debit is entered, clear credit and vice versa
    if (field === 'debit' && value > 0) newLines[index].credit = 0;
    if (field === 'credit' && value > 0) newLines[index].debit = 0;

    // Handle tax auto-calculation
    if (field === 'taxRate' && value !== '0%') {
      const taxRateObj = taxRates.find(t => t.name === value);
      if (taxRateObj) {
        const rate = parseFloat(taxRateObj.rate || '0') / 100;
        const baseAmount = newLines[index].debit > 0 ? newLines[index].debit : newLines[index].credit;
        if (baseAmount > 0) {
          const taxAmount = baseAmount * rate;
          const isDebit = newLines[index].debit > 0;
          newLines.push({
            accountId: '',
            accountName: '',
            description: `Tax (${taxRateObj.rate}%)`,
            debit: isDebit ? taxAmount : 0,
            credit: !isDebit ? taxAmount : 0,
            taxRate: '0%'
          });
        }
      }
    }

    const { totalDebit, totalCredit } = calculateTotals(newLines);
    setNewJournal({ ...newJournal, lines: newLines, totalDebit, totalCredit });
  };

  const handleSaveJournal = async (e: React.FormEvent, statusOverride?: 'Draft' | 'Posted', addAnother = false) => {
    if (e) e.preventDefault();
    if (newJournal.totalDebit !== newJournal.totalCredit) {
      notify.error("Journal must be balanced (Total Debit must equal Total Credit)");
      return;
    }
    if (newJournal.totalDebit === 0) {
      notify.error("Journal amount cannot be zero");
      return;
    }
    if (newJournal.lines.some(l => !l.accountId)) {
      notify.error("Please select an account for all lines");
      return;
    }

    setIsSaving(true);
    try {
      const journalData = {
        ...newJournal,
        orgId,
        status: statusOverride || newJournal.status,
        updatedAt: serverTimestamp()
      };
      const localAuthToken = getLocalAuthToken();

      if (localAuthToken && isEditing && selectedJournal && statusOverride === 'Posted') {
        const response = await fetch(`/api/organizations/${orgId}/manual-journals/${selectedJournal.id}/post`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${localAuthToken}` }
        });
        if (!response.ok) {
          throw new Error((await response.text()) || `Request failed (${response.status})`);
        }
        const postedJournal = { ...selectedJournal, ...newJournal, status: 'Posted' } as ManualJournal;
        setJournals(current => current.map(journal =>
          journal.id === selectedJournal.id ? postedJournal : journal
        ));
        setSelectedJournal(postedJournal);
        setIsModalOpen(false);
        setIsEditing(false);
        notify.confirm("Journal posted");
        return;
      }

      if (localAuthToken && !isEditing) {
        const localPayload = {
          journalNumber: newJournal.journalNumber,
          date: newJournal.date,
          narration: newJournal.narration,
          description: newJournal.narration,
          status: (statusOverride || newJournal.status).toLowerCase(),
          repeatingConfig: repeatEnabled ? { enabled: true, frequency: repeatFrequency, frequencyUnit: repeatUnit, startDate: newJournal.date, endDate: repeatEndDate || null } : undefined,
          lines: newJournal.lines.map(line => ({
            accountId: line.accountId,
            accountName: line.accountName,
            description: line.description,
            debit: line.debit,
            credit: line.credit
          }))
        };
        const response = await fetch(`/api/organizations/${orgId}/manual-journals`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${localAuthToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(localPayload)
        });
        if (!response.ok) {
          throw new Error((await response.text()) || `Request failed (${response.status})`);
        }
        const createdJournal = await response.json();
        setJournals(current => [{
          ...newJournal,
          id: createdJournal.id,
          status: statusOverride || newJournal.status
        } as ManualJournal, ...current]);
        if (addAnother) {
          setNewJournal({ ...newJournal, journalNumber: `JNL-${Math.floor(1000 + Math.random() * 9000)}`, narration: '', lines: [{ id: uuidv4(), accountId: '', accountName: '', description: '', debit: 0, credit: 0 }, { id: uuidv4(), accountId: '', accountName: '', description: '', debit: 0, credit: 0 }], totalDebit: 0, totalCredit: 0 });
          setRepeatEnabled(false); setRepeatEndDate('');
        } else setIsModalOpen(false);
        notify.confirm(addAnother ? "Journal saved — ready for another" : (statusOverride === 'Posted' ? "Journal posted" : "Journal saved as draft"));
        return;
      }

      if (isEditing && selectedJournal) {
        await updateDoc(doc(db, 'manual_journals', selectedJournal.id), journalData);
      } else {
        await addDoc(collection(db, 'manual_journals'), {
          ...journalData,
          createdBy: auth.currentUser?.uid,
          createdAt: serverTimestamp()
        });
      }
      setIsModalOpen(false);
      setIsEditing(false);
      notify.confirm(isEditing ? "Journal updated" : (statusOverride === 'Posted' ? "Journal posted" : "Journal saved as draft"));
    } catch (error) {
      if (getLocalAuthToken() && !isEditing) {
        console.error("Failed to save local journal:", error);
      } else {
        handleFirestoreError(error, isEditing ? OperationType.UPDATE : OperationType.CREATE, 'manual_journals');
      }
      notify.error("Failed to save journal");
    } finally {
      setIsSaving(false);
    }
  };

  const handleReverseJournal = async (journal: ManualJournal) => {
    const localAuthToken = getLocalAuthToken();
    if (!localAuthToken || !journal.id) return;

    setIsSaving(true);
    try {
      const response = await fetch(`/api/organizations/${orgId}/manual-journals/${journal.id}/reverse`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localAuthToken}` }
      });
      if (!response.ok) {
        throw new Error((await response.text()) || `Request failed (${response.status})`);
      }

      const reversedJournal = { ...journal, status: 'Reversed' as ManualJournal['status'] } as ManualJournal;
      setJournals(current => current.map(currentJournal =>
        currentJournal.id === journal.id ? reversedJournal : currentJournal
      ));
      setSelectedJournal(reversedJournal);
      notify.confirm("Journal reversed");
    } catch (error) {
      console.error("Failed to reverse local journal:", error);
      notify.error("Failed to reverse journal");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteJournal = async (id: string) => {
    try {
      await deleteDoc(doc(db, 'manual_journals', id));
      notify.confirm("Journal deleted");
      setDeleteId(null);
    } catch (error) {
      handleFirestoreError(error, OperationType.DELETE, 'manual_journals');
      notify.error("Failed to delete journal");
    }
  };

  const filteredJournals = journals.filter(journal => {
    const matchesSearch = journal.narration.toLowerCase().includes(searchTerm.toLowerCase()) || 
                         journal.journalNumber.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'All' || journal.status === statusFilter;
    
    const matchesDate = (!startDate || journal.date >= startDate) && 
                        (!endDate || journal.date <= endDate);
    
    const matchesAmount = (!minAmount || journal.totalDebit >= parseFloat(minAmount)) && 
                          (!maxAmount || journal.totalDebit <= parseFloat(maxAmount));

    return matchesSearch && matchesStatus && matchesDate && matchesAmount;
  });

  const handleExportCSV = () => {
    const data = filteredJournals.map(j => ({
      'Date': j.date,
      'Journal Number': j.journalNumber,
      'Narration': j.narration,
      'Total Debit': j.totalDebit,
      'Total Credit': j.totalCredit,
      'Status': j.status
    }));
    exportToCSV(data, `Manual_Journals_${new Date().toISOString().split('T')[0]}.csv`);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Manual Journals</h1>
          <p className="text-sm opacity-50 italic serif mt-1">Create and manage manual ledger adjustments</p>
        </div>
        <div className="flex items-center gap-3">
          <button 
            onClick={handleExportCSV}
            className="flex items-center gap-2 px-4 py-3 border border-[#141414] rounded-full hover:bg-[#141414] hover:text-white transition-all text-xs font-bold"
          >
            <Download className="w-4 h-4" />
            <span>Export CSV</span>
          </button>
          <button 
            onClick={() => {
            setIsEditing(false);
            setNewJournal({
              journalNumber: `JNL-${Math.floor(1000 + Math.random() * 9000)}`,
              date: new Date().toISOString().split('T')[0],
              narration: '',
              status: 'Draft',
              lines: [
                { accountId: '', accountName: '', description: '', debit: 0, credit: 0, taxRate: '0%' },
                { accountId: '', accountName: '', description: '', debit: 0, credit: 0, taxRate: '0%' }
              ],
              totalDebit: 0,
              totalCredit: 0
            });
            setIsModalOpen(true);
          }}
          className="flex items-center gap-2 bg-[#141414] text-[#E4E3E0] px-6 py-3 rounded-full hover:opacity-90 transition-opacity font-bold"
        >
          <Plus className="w-4 h-4" />
          <span>{language === 'ar' ? 'قيد جديد' : 'New Journal'}</span>
        </button>
      </div>
    </div>

    {/* Filters & Search */}
    <div className="space-y-4">
        <div className="flex flex-col md:flex-row gap-4 items-center justify-between bg-white/50 border border-[#141414] p-4 rounded-2xl">
          <div className="flex items-center gap-2 overflow-x-auto pb-2 md:pb-0 w-full md:w-auto custom-scrollbar">
            {['All', 'Draft', 'Posted', 'Void'].map((status) => (
              <button
                key={status}
                onClick={() => setStatusFilter(status)}
                className={cn(
                  "px-4 py-2 rounded-full text-xs font-bold transition-all whitespace-nowrap border shrink-0",
                  statusFilter === status 
                    ? "bg-[#141414] text-[#E4E3E0] border-[#141414]" 
                    : "bg-white/50 text-[#141414] border-[#141414]/10 hover:border-[#141414]"
                )}
              >
                {status}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-3 w-full md:w-auto">
            <div className="relative flex-1 md:w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 opacity-50" />
              <input 
                type="text" 
                placeholder="Search journals..." 
                className="w-full pl-10 pr-4 py-2 bg-white/50 border border-[#141414]/10 rounded-full outline-none focus:border-[#141414] transition-all text-sm font-bold"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <button 
              onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
              className={cn(
                "p-2.5 rounded-full border transition-all shadow-sm",
                showAdvancedFilters || startDate || endDate || minAmount || maxAmount
                  ? "bg-[#141414] text-white border-[#141414]" 
                  : "bg-white text-gray-500 border-gray-200 hover:border-gray-400"
              )}
            >
              <Filter className="w-4 h-4" />
            </button>
          </div>
        </div>

        {showAdvancedFilters && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 bg-white border border-[#141414]/10 p-4 rounded-2xl animate-in slide-in-from-top-2 shadow-sm">
            <div className="space-y-1.5">
              <label className="text-[10px] uppercase font-bold opacity-40 tracking-widest">Start Date</label>
              <input 
                type="date"
                value={startDate}
                onChange={e => setStartDate(e.target.value)}
                className="w-full bg-white border border-gray-200 rounded-xl px-3 py-2 text-xs font-bold outline-none focus:ring-2 ring-gray-100"
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-[10px] uppercase font-bold opacity-40 tracking-widest">End Date</label>
              <input 
                type="date"
                value={endDate}
                onChange={e => setEndDate(e.target.value)}
                className="w-full bg-white border border-gray-200 rounded-xl px-3 py-2 text-xs font-bold outline-none focus:ring-2 ring-gray-100"
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-[10px] uppercase font-bold opacity-40 tracking-widest">Amount Range</label>
              <div className="grid grid-cols-2 gap-2">
                <input 
                  type="number" 
                  placeholder="Min"
                  value={minAmount}
                  onChange={e => setMinAmount(e.target.value)}
                  className="w-full bg-white border border-gray-200 rounded-xl px-3 py-2 text-xs font-bold outline-none focus:ring-2 ring-gray-100"
                />
                <input 
                  type="number" 
                  placeholder="Max"
                  value={maxAmount}
                  onChange={e => setMaxAmount(e.target.value)}
                  className="w-full bg-white border border-gray-200 rounded-xl px-3 py-2 text-xs font-bold outline-none focus:ring-2 ring-gray-100"
                />
              </div>
            </div>
            <div className="flex items-end">
              <button 
                onClick={() => {
                  setStartDate('');
                  setEndDate('');
                  setMinAmount('');
                  setMaxAmount('');
                  setSearchTerm('');
                }}
                className="w-full py-2 bg-red-50 text-red-500 rounded-xl text-[10px] font-bold uppercase tracking-widest hover:bg-red-100 transition-colors"
              >
                Reset Filters
              </button>
            </div>
          </div>
        )}
      </div>

      {isLoading ? (
        <div className="flex flex-col items-center justify-center py-20 opacity-50">
          <Loader2 className="w-12 h-12 animate-spin mb-4" />
          <p className="text-xl italic serif">Loading journals...</p>
        </div>
      ) : filteredJournals.length === 0 ? (
        <div className="text-center py-20 bg-white/30 border-2 border-dashed border-[#141414]/20 rounded-3xl">
          <FileText className="w-16 h-16 mx-auto mb-4 opacity-20" />
          <p className="text-xl font-bold opacity-50 italic serif">No journals found</p>
          <p className="text-sm opacity-30 mt-2">Create your first manual journal entry</p>
        </div>
      ) : (
        <div className="bg-white/50 border border-[#141414] rounded-3xl overflow-hidden">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-[#141414] text-[#E4E3E0] text-[10px] uppercase tracking-widest font-bold">
                <th className="px-6 py-4">{language === 'ar' ? 'التاريخ' : 'Date'}</th>
                <th className="px-6 py-4">Number</th>
                <th className="px-6 py-4">{language === 'ar' ? 'البيان' : 'Narration'}</th>
                <th className="px-6 py-4 text-right">{language === 'ar' ? 'المبلغ' : 'Amount'}</th>
                <th className="px-6 py-4">Status</th>
                <th className="px-6 py-4"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#141414]/5">
              {filteredJournals.map((journal) => (
                <tr 
                  key={journal.id}
                  className="hover:bg-[#141414]/5 transition-colors group cursor-pointer"
                  onClick={() => {
                    setSelectedJournal(journal);
                    setIsViewModalOpen(true);
                  }}
                >
                  <td className="px-6 py-4 text-sm font-bold">{journal.date}</td>
                  <td className="px-6 py-4 text-sm font-mono opacity-50">{journal.journalNumber}</td>
                  <td className="px-6 py-4 text-sm italic serif">{journal.narration}</td>
                  <td className="px-6 py-4 text-sm font-bold text-right">{formatCurrency(journal.totalDebit, currency)}</td>
                  <td className="px-6 py-4">
                    <span className={cn(
                      "px-3 py-1 rounded-full text-[10px] font-bold border",
                      journal.status === 'Posted' ? "bg-green-100 text-green-700 border-green-200" : 
                      journal.status === 'Draft' ? "bg-blue-100 text-blue-700 border-blue-200" :
                      "bg-gray-100 text-gray-700 border-gray-200"
                    )}>
                      {journal.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex items-center justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button 
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedJournal(journal);
                          setNewJournal({ ...journal });
                          setIsEditing(true);
                          setIsModalOpen(true);
                        }}
                        className="p-2 hover:bg-[#141414] hover:text-[#E4E3E0] rounded-full transition-colors"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button 
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeleteId(journal.id!);
                        }}
                        className="p-2 hover:bg-red-500 hover:text-white rounded-full transition-colors text-red-500"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* View Journal Modal */}
      {isViewModalOpen && selectedJournal && (
        <div
          className="fixed inset-0 overflow-y-auto overscroll-contain bg-[#141414]/80 backdrop-blur-sm z-[110] flex items-center justify-center p-4"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setIsViewModalOpen(false);
          }}
        >
          <div className="bg-[#E4E3E0] border border-[#141414] w-full max-w-4xl rounded-3xl shadow-2xl overflow-hidden flex flex-col max-h-[calc(100dvh-2rem)]">
            <div className="p-6 border-b border-[#141414] flex items-center justify-between bg-[#141414] text-[#E4E3E0]">
              <div className="flex items-center gap-3">
                <FileText className="w-6 h-6" />
                <div>
                  <h2 className="text-xl font-bold italic serif">Journal: {selectedJournal.journalNumber}</h2>
                  <p className="text-[10px] uppercase tracking-widest opacity-60">{new Date(selectedJournal.date).toLocaleDateString()}</p>
                </div>
              </div>
              <button 
                onClick={() => setIsViewModalOpen(false)}
                className="p-2 hover:bg-white/10 rounded-full transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="flex-1 overflow-y-auto p-8 space-y-8 custom-scrollbar">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="space-y-4">
                  <div>
                    <label className="text-[10px] font-bold uppercase tracking-widest opacity-40 block mb-1">{language === 'ar' ? 'البيان' : 'Narration'}</label>
                    <p className="text-sm italic serif opacity-80 leading-relaxed">{selectedJournal.narration || 'No narration provided.'}</p>
                  </div>
                  <div>
                    <label className="text-[10px] font-bold uppercase tracking-widest opacity-40 block mb-1">Status</label>
                    <span className={cn(
                      "px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-tighter",
                      selectedJournal.status === 'Posted' ? "bg-green-100 text-green-700" : 
                      selectedJournal.status === 'Draft' ? "bg-gray-100 text-gray-700" : "bg-red-100 text-red-700"
                    )}>{selectedJournal.status}</span>
                  </div>
                </div>
                
                <div className="bg-[#141414] text-[#E4E3E0] p-6 rounded-2xl flex flex-col justify-center">
                  <div className="flex justify-between items-center mb-4 opacity-50">
                    <span className="text-[10px] uppercase tracking-widest font-bold">Total Balance</span>
                    <CheckCircle2 className="w-4 h-4" />
                  </div>
                  <div className="flex justify-between items-end">
                    <div>
                      <p className="text-[10px] uppercase tracking-widest opacity-40 mb-1">{language === 'ar' ? 'مدين' : 'Debit'}</p>
                      <p className="text-2xl font-bold font-mono">{formatCurrency(selectedJournal.totalDebit, currency)}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-[10px] uppercase tracking-widest opacity-40 mb-1">{language === 'ar' ? 'دائن' : 'Credit'}</p>
                      <p className="text-2xl font-bold font-mono">{formatCurrency(selectedJournal.totalCredit, currency)}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <label className="text-[10px] font-bold uppercase tracking-widest opacity-40 block mb-1">Journal Lines</label>
                <div className="border border-[#141414] rounded-2xl overflow-hidden">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="bg-[#141414] text-[#E4E3E0] text-[10px] uppercase tracking-widest">
                        <th className="p-4 font-bold">{language === 'ar' ? 'الحساب' : 'Account'}</th>
                        <th className="p-4 font-bold">{language === 'ar' ? 'جهة الاتصال' : 'Partner'}</th>
                        <th className="p-4 font-bold">{language === 'ar' ? 'الوصف' : 'Description'}</th>
                        <th className="p-4 font-bold text-right">{language === 'ar' ? 'مدين' : 'Debit'}</th>
                        <th className="p-4 font-bold text-right">{language === 'ar' ? 'دائن' : 'Credit'}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(selectedJournal.lines || []).map((line: any, idx: number) => (
                        <tr key={line.id || idx} className="border-b border-[#141414]/10 text-sm">
                          <td className="p-4 font-bold">{line.accountName}</td>
                          <td className="p-4 font-bold">{line.contactName || '—'}</td>
                          <td className="p-4 opacity-70 italic serif">{line.description}</td>
                          <td className="p-4 text-right font-mono">{line.debit > 0 ? formatCurrency(line.debit, currency) : '—'}</td>
                          <td className="p-4 text-right font-mono">{line.credit > 0 ? formatCurrency(line.credit, currency) : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="pt-8 border-t border-[#141414]">
                <HistoryNotesView orgId={orgId} entityId={selectedJournal.id!} entityType="ManualJournal" />
              </div>
            </div>
            
            <div className="p-6 bg-[#141414]/5 border-t border-[#141414] flex justify-end gap-3">
              {getLocalAuthToken() && selectedJournal.status === 'Posted' && (
                <button
                  onClick={() => handleReverseJournal(selectedJournal)}
                  disabled={isSaving}
                  className="px-6 py-2 bg-red-50 text-red-600 border border-red-200 rounded-xl text-xs font-bold uppercase tracking-widest hover:bg-red-100 transition-all disabled:opacity-50"
                >
                  Reverse Journal
                </button>
              )}
              <button 
                onClick={() => {
                  setIsViewModalOpen(false);
                  setSelectedJournal(selectedJournal);
                  setIsEditing(true);
                  setIsModalOpen(true);
                }}
                className="px-6 py-2 bg-white border border-[#141414] rounded-xl text-xs font-bold uppercase tracking-widest hover:bg-[#141414] hover:text-[#E4E3E0] transition-all"
              >{language === 'ar' ? 'تعديل القيد' : 'Edit Journal'}</button>
              <button 
                onClick={() => setIsViewModalOpen(false)}
                className="px-6 py-2 bg-[#141414] text-[#E4E3E0] rounded-xl text-xs font-bold uppercase tracking-widest hover:opacity-90 transition-all"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Journal Modal */}
      {isModalOpen && (
        <div
          className="fixed inset-0 overflow-y-auto overscroll-contain bg-[#141414]/40 backdrop-blur-sm z-[100] flex items-center justify-center p-4"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setIsModalOpen(false);
          }}
        >
          <div className="bg-[#E4E3E0] border border-[#141414] w-full max-w-6xl max-h-[calc(100dvh-2rem)] rounded-3xl overflow-hidden flex flex-col shadow-2xl">
            <div className="p-6 border-b border-[#141414] flex items-center justify-between bg-white/50">
              <h2 className="text-xl font-bold italic serif">
                {isEditing ? `Edit Journal: ${newJournal.journalNumber}` : 'New Manual Journal'}
              </h2>
              <button 
                onClick={() => setIsModalOpen(false)}
                className="p-2 hover:bg-[#141414] hover:text-[#E4E3E0] rounded-full transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={(e) => handleSaveJournal(e)} className="flex-1 overflow-y-auto p-8 space-y-8 custom-scrollbar bg-white/50">
              
              <div className="flex items-start justify-between mb-8">
                <div>
                  <h1 className="text-3xl font-bold font-mono tracking-tight">{isEditing ? newJournal.journalNumber : 'Draft'}</h1>
                </div>
                <div className="flex items-center gap-2">
                  <span className={cn(
                    "px-3 py-1 rounded-full text-xs font-bold uppercase tracking-widest border",
                    newJournal.status === 'Draft' ? 'bg-gray-100 text-gray-700 border-gray-200' : 
                    newJournal.status === 'Posted' ? 'bg-green-100 text-green-700 border-green-200' :
                    'bg-red-100 text-red-700 border-red-200'
                  )}>
                    {newJournal.status}
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-4">
                <div className="space-y-4">
                  <div className="flex items-center gap-4">
                    <label className="w-32 text-sm font-bold opacity-70 border-b border-transparent">Reference (Auto)</label>
                    <input 
                      required
                      type="text" 
                      className="flex-1 bg-transparent border-b border-[#141414]/20 p-2 outline-none focus:border-[#141414] transition-all font-bold"
                      value={newJournal.journalNumber}
                      onChange={(e) => setNewJournal({...newJournal, journalNumber: e.target.value})}
                      placeholder="e.g. MISC/2026/05/0001"
                    />
                  </div>
                  <div className="flex items-center gap-4">
                    <label className="w-32 text-sm font-bold opacity-70 border-b border-transparent">Description</label>
                    <input 
                      required
                      type="text" 
                      className="flex-1 bg-transparent border-b border-[#141414]/20 p-2 outline-none focus:border-[#141414] transition-all font-bold"
                      value={newJournal.narration}
                      onChange={(e) => setNewJournal({...newJournal, narration: e.target.value})}
                      placeholder="e.g. MISC/2026/0001 details..."
                    />
                  </div>
                  <div className="flex items-center gap-4">
                    <label className="w-32 text-sm font-bold opacity-70 border-b border-transparent">Journal Book</label>
                    <select 
                      className="flex-1 bg-transparent border-b border-[#141414]/20 p-2 outline-none focus:border-[#141414] transition-all font-bold"
                      value={newJournal.journalBookId || ''}
                      onChange={(e) => {
                        const selectedBook = journalBooks.find(b => b.id === e.target.value);
                        const bookCode = selectedBook?.code || 'MISC';
                        const date = new Date(newJournal.date);
                        const year = date.getFullYear();
                        const month = String(date.getMonth() + 1).padStart(2, '0');
                        
                        // Find current max sequence for this book/month/year (rough estimate)
                        const relevantJournals = journals.filter(j => 
                          j.journalBookId === e.target.value && 
                          new Date(j.date).getFullYear() === year && 
                          (new Date(j.date).getMonth() + 1) === (date.getMonth() + 1)
                        );
                        const nextSeq = String(relevantJournals.length + 1).padStart(4, '0');
                        const newRef = `${bookCode}/${year}/${month}/${nextSeq}`;

                        setNewJournal({
                          ...newJournal, 
                          journalBookId: e.target.value, 
                          journalBookName: selectedBook?.name || '',
                          journalNumber: newRef
                        });
                      }}
                    >
                      <option value="">Select Journal Type...</option>
                      {journalBooks.map(book => (
                        <option key={book.id} value={book.id}>{book.name} {book.code ? `(${book.code})` : ''}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="flex items-center gap-4">
                    <label className="w-32 text-sm font-bold opacity-70 border-b border-transparent">Accounting Date</label>
                    <input 
                      required
                      type="date" 
                      className="flex-1 bg-transparent border-b border-[#141414]/20 p-2 outline-none focus:border-[#141414] transition-all font-bold"
                      value={newJournal.date}
                      onChange={(e) => setNewJournal({...newJournal, date: e.target.value})}
                    />
                  </div>
                  <div className="flex items-center gap-4">
                    <label className="w-32 text-sm font-bold opacity-70 border-b border-transparent">Journal</label>
                    <select className="flex-1 bg-transparent border-b border-[#141414]/20 p-2 outline-none focus:border-[#141414] transition-all font-bold cursor-pointer">
                      <option>Miscellaneous Operations</option>
                      <option>Cash</option>
                      <option>Bank</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="rounded-2xl border border-[#141414]/10 bg-white p-4 space-y-3">
                <label className="flex items-center gap-3 text-sm font-bold"><input type="checkbox" checked={repeatEnabled} onChange={e => setRepeatEnabled(e.target.checked)} /> Repeat this journal automatically</label>
                {repeatEnabled && <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
                  <label className="space-y-1">Every<input type="number" min="1" value={repeatFrequency} onChange={e => setRepeatFrequency(Math.max(1, Number(e.target.value) || 1))} className="w-full border-b border-[#141414]/20 p-2 outline-none" /></label>
                  <label className="space-y-1">Period<select value={repeatUnit} onChange={e => setRepeatUnit(e.target.value as 'Week' | 'Month')} className="w-full border-b border-[#141414]/20 p-2 outline-none"><option value="Month">Month(s)</option><option value="Week">Week(s)</option></select></label>
                  <label className="space-y-1">End date (optional)<input type="date" value={repeatEndDate} min={newJournal.date} onChange={e => setRepeatEndDate(e.target.value)} className="w-full border-b border-[#141414]/20 p-2 outline-none" /></label>
                </div>}
              </div>

              {/* Journal Lines */}
              <div className="space-y-4 bg-white rounded-2xl p-4 shadow-sm border border-[#141414]/10 mt-8">
                <div className="flex items-center justify-between border-b border-[#141414]/10 pb-4 mb-2">
                  <h3 className="text-sm font-bold opacity-70">Journal Items</h3>
                  <button type="button" onClick={addLine} className="text-sm font-bold text-primary hover:opacity-70 transition-opacity flex items-center gap-1">

                    <Plus className="w-3 h-3" /> Add Line
                  </button>
                </div>

                <div className="space-y-2">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="border-b border-[#141414]/10 text-[10px] uppercase tracking-widest font-bold opacity-40">
                        <th className="p-2 w-1/4">{language === 'ar' ? 'الحساب' : 'Account'}</th>
                        <th className="p-2 w-[18%]">{language === 'ar' ? 'جهة الاتصال' : 'Partner'}</th>
                        <th className="p-2 w-[12%]">{language === 'ar' ? 'الضريبة' : 'Tax'}</th>
                        <th className="p-2 w-1/4">{language === 'ar' ? 'البيان' : 'Label'}</th>
                        <th className="p-2 w-28 text-right">{language === 'ar' ? 'مدين' : 'Debit'}</th>
                        <th className="p-2 w-28 text-right">{language === 'ar' ? 'دائن' : 'Credit'}</th>
                        <th className="p-2 w-8"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#141414]/10">
                      {(newJournal.lines || []).map((line: any, index) => (
                        <tr key={line.id || index} className="group hover:bg-[#141414]/5 transition-colors">
                          <td className="p-2">
                            <div className="flex items-center gap-2">
                              <input 
                                required
                                type="text"
                                list={`accounts-list-${index}`}
                                className="w-full bg-transparent border-b border-[#141414]/20 outline-none text-xs font-bold focus:border-[#141414] pb-1"
                                placeholder="Account Name or Code"
                                value={line.accountName || ''}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  const accountMatch = accounts.find(a => `${a.code} - ${a.name}` === val);
                                  if (accountMatch) {
                                    updateLine(index, 'accountId', accountMatch.id);
                                    const newLines = [...newJournal.lines];
                                    newLines[index].accountName = val;
                                    setNewJournal({ ...newJournal, lines: newLines });
                                  } else {
                                    updateLine(index, 'accountId', '');
                                    const newLines = [...newJournal.lines];
                                    newLines[index].accountName = val;
                                    setNewJournal({ ...newJournal, lines: newLines });
                                  }
                                }}
                              />
                              <datalist id={`accounts-list-${index}`}>
                                {accounts.map(acc => (
                                  <option key={acc.id} value={`${acc.code} - ${acc.name}`} />
                                ))}
                              </datalist>
                              {!line.accountId && line.accountName && line.accountName.trim() !== '' && (
                                <button 
                                  type="button"
                                  onClick={() => {
                                    setQuickAccountData({ ...quickAccountData, name: line.accountName });
                                    setTargetLineIndex(index);
                                    setIsQuickAccountModalOpen(true);
                                  }}
                                  className="p-1 bg-[#141414] text-white rounded-md hover:bg-black/80 flex-shrink-0"
                                >
                                  <Plus className="w-3 h-3" />
                                </button>
                              )}
                            </div>
                          </td>
                          <td className="p-2">
                            <select 
                              className="w-full bg-transparent border-b border-[#141414]/20 outline-none text-xs font-bold focus:border-[#141414] pb-1"
                              value={line.contactId || ''}
                              onChange={(e) => updateLine(index, 'contactId', e.target.value)}
                            >
                              <option value="">Select Partner...</option>
                              {contacts.map(c => (
                                <option key={c.id} value={c.id}>{c.name}</option>
                              ))}
                            </select>
                          </td>
                          <td className="p-2">
                            <select 
                              className="w-full bg-transparent border-b border-[#141414]/20 outline-none text-xs font-bold focus:border-[#141414] pb-1"
                              value={line.taxRate || '0%'}
                              onChange={(e) => updateLine(index, 'taxRate', e.target.value)}
                            >
                              <option value="0%">Tax Exempt (0%)</option>
                              {taxRates.map(tax => (
                                <option key={tax.id} value={tax.name}>{tax.name} ({tax.rate}%) - {tax.type || 'Both'}</option>
                              ))}
                            </select>
                          </td>
                          <td className="p-2">
                            <input 
                              type="text" 
                              className="w-full bg-transparent border-b border-[#141414]/20 outline-none text-xs focus:border-[#141414] pb-1"
                              placeholder="Label"
                              value={line.description}
                              onChange={(e) => updateLine(index, 'description', e.target.value)}
                            />
                          </td>
                          <td className="p-2">
                            <input 
                              type="number" 
                              step="0.01"
                              className="w-full bg-transparent border-b border-[#141414]/20 outline-none text-xs text-right font-bold w-[100px] focus:border-[#141414] pb-1"
                              value={isNaN(line.debit) ? '' : (line.debit || '')}
                              onChange={(e) => updateLine(index, 'debit', e.target.value === '' ? 0 : parseFloat(e.target.value))}
                            />
                          </td>
                          <td className="p-2">
                            <input 
                              type="number" 
                              step="0.01"
                              className="w-full bg-transparent border-b border-[#141414]/20 outline-none text-xs text-right font-bold w-[100px] focus:border-[#141414] pb-1"
                              value={isNaN(line.credit) ? '' : (line.credit || '')}
                              onChange={(e) => updateLine(index, 'credit', e.target.value === '' ? 0 : parseFloat(e.target.value))}
                            />
                          </td>
                          <td className="p-2 text-center">
                            <button 
                              type="button" 
                              onClick={() => removeLine(index)}
                              className="p-1 text-red-500 opacity-50 hover:opacity-100 transition-opacity"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="border-t border-[#141414]/10 bg-[#141414]/5">
                         <td colSpan={4} className="p-4 text-left">
                           <button type="button" onClick={addLine} className="text-sm font-bold text-[#141414] hover:text-[#141414]/70 transition-colors flex items-center gap-1">
                             <Plus className="w-3 h-3" /> {language === 'ar' ? 'إضافة سطر' : 'Add Line'}
                           </button>
                         </td>
                         <td className="p-4 text-right text-sm font-bold border-b-[3px] border-[#141414] w-[100px]">
                           ${newJournal.totalDebit.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                         </td>
                         <td className="p-4 text-right text-sm font-bold border-b-[3px] border-[#141414] w-[100px]">
                           ${newJournal.totalCredit.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                         </td>
                         <td></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>

                {newJournal.totalDebit !== newJournal.totalCredit && (
                  <div className="flex items-center gap-2 text-red-500 text-xs font-bold justify-end mt-2">
                    <AlertCircle className="w-4 h-4" />
                    <span>Out of balance by ${Math.abs(newJournal.totalDebit - newJournal.totalCredit).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                  </div>
                )}
              </div>

              <div className="pt-8 border-t border-[#141414] flex items-center justify-end gap-4">
                <button 
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="px-8 py-4 rounded-full font-bold hover:bg-[#141414]/5 transition-colors"
                >{language === 'ar' ? 'إلغاء' : 'Cancel'}</button>
                <button 
                  type="button"
                  onClick={(e) => handleSaveJournal(e as any, 'Draft')}
                  disabled={isSaving || newJournal.totalDebit !== newJournal.totalCredit || !newJournal.narration.trim()}
                  className="px-8 py-4 rounded-full font-bold border border-[#141414] hover:bg-[#141414] hover:text-[#E4E3E0] transition-all disabled:opacity-30"
                >
                  Save as Draft
                </button>
                {!isEditing && <button
                  type="button"
                  onClick={(e) => handleSaveJournal(e as any, 'Draft', true)}
                  disabled={isSaving || newJournal.totalDebit !== newJournal.totalCredit || !newJournal.narration.trim()}
                  className="px-8 py-4 rounded-full font-bold border border-[#141414]/30 hover:bg-[#141414]/10 transition-all disabled:opacity-30"
                >
                  Save &amp; Add Another
                </button>}
                <button 
                  type="button"
                  onClick={(e) => handleSaveJournal(e as any, 'Posted')}
                  disabled={isSaving || newJournal.totalDebit !== newJournal.totalCredit}
                  className="bg-[#141414] text-[#E4E3E0] px-12 py-4 rounded-full font-bold hover:opacity-90 transition-opacity flex items-center gap-2 disabled:opacity-30"
                >
                  {isSaving && <Loader2 className="w-4 h-4 animate-spin" />}
                  <span>{isEditing ? 'Update Journal' : 'Post Journal'}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {deleteId && (
        <ConfirmModal
          isOpen={!!deleteId}
          onClose={() => setDeleteId(null)}
          onConfirm={() => handleDeleteJournal(deleteId)}
          title="Delete Journal"
          message="Are you sure you want to delete this journal entry? This action cannot be undone."
        />
      )}
    </div>
  );
}
